import { get } from 'lodash';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { ComparatorModel, getBestComparatorVehiclesPath, NO_STYLE_ID } from 'client/data/models/comparator';
import { buildSegmentRatingsPath, TypeRankingsFeaturesModel } from 'client/data/models/type-rankings-features';
import { VehicleModel } from 'client/data/models/vehicle-v2';
import { CmsModel } from 'client/data/models/cms';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { buildMMYSubmodelTrimPhotosPathWithParams, PhotosModel } from 'client/data/models/photos';
import { updateVehicle } from 'client/actions/vehicle';
import { setCriticalCss } from 'client/actions/critical-css';
import { getFAQSchema } from 'client/seo/schemas/faq';
import { parseVehicleForAction } from 'client/utils/parse-vehicle-for-action';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { EdmundsDefaultDecorator } from 'site-modules/shared/pages/decorators/edmunds-default';
import { setAdsListToPreload } from 'site-modules/shared/components/native-ad/utils/utils';
import { INCENTIVES_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { MAX_COMPARE_SIZE } from 'site-modules/comparator/constants/comparator';
import { COMPARATOR_FAQ_CONTENT } from 'site-modules/comparator/constants/faq-content';
import { buildVehiclePath, getColumnIdsString, getTCOIdsString } from 'site-modules/comparator/utils/comparator';
import { decoratorParams } from 'site-modules/shared/pages/decorators/decorator-params';
import { Comparator } from './comparator';

export const pageOptions = {
  name: 'comparator',
  category: 'comparator',
  component: Comparator,
  decorator: decoratorParams(EdmundsDefaultDecorator, { skipFloodlight: true }),
  seo: (state, props) => ({
    title: 'Compare Cars Side-by-Side - Car Comparison Tool | Edmunds',
    description:
      'Compare cars side by side to find the right vehicle for you. Compare car prices, expert and consumer ratings, features, warranties, fuel economy, incentives and more.',
    jsonld: [
      getFAQSchema({
        entries: COMPARATOR_FAQ_CONTENT,
        learnMoreLinkText: 'Compare Cars Here',
        pathname: get(props, 'location.pathname'),
        withTracking: true,
      }),
    ],
  }),
  preload: (store, props) => {
    const preloader = new ModelPreloader(store, props);

    preloader.resolve('content["comparator/compare-venom"]', CmsModel);
    preloader.resolve('suggestedLinks', ComparatorModel);
    preloader.resolve(getBestComparatorVehiclesPath(), ComparatorModel);
    new Array(MAX_COMPARE_SIZE)
      .fill()
      .forEach((value, index) => preloader.resolve(buildVehiclePath(props.location, index + 1), VehicleModel));
    preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

    const adsList = [INCENTIVES_AD.AD_NAME];
    preloader.set('adsListSkipExtraParams', NativeAdsCreativeConfigModel, adsList);
    preloader.set('pageAdsList', NativeAdsCreativeConfigModel, adsList);

    return preloader.load().then(modelState => {
      const columns = [];
      const vehicles = [];

      new Array(MAX_COMPARE_SIZE).fill().forEach((value, index) => {
        const vehicle = modelState.get(buildVehiclePath(props.location, index + 1), VehicleModel);
        const vehicleId = get(vehicle, 'id');

        columns.push({ styleId: vehicleId });
        vehicles.push(vehicle);

        if (!index) {
          store.dispatch(updateVehicle(parseVehicleForAction(vehicleId ? vehicle : {})));
        }

        if (!vehicleId) {
          return;
        }

        const { makeSlug, modelSlug, year, submodels, trimName } = vehicle;

        preloader.resolve(
          buildMMYSubmodelTrimPhotosPathWithParams({
            make: makeSlug,
            model: modelSlug,
            submodel: get(submodels, 'name'),
            trim: trimName,
            year,
          }),
          PhotosModel
        );

        setAdsListToPreload(preloader, store.getState(), adsList, parseVehicleForAction(vehicle), {
          skipSetList: true,
        });

        preloader.resolve(buildSegmentRatingsPath({ makeSlug, modelSlug, year }), TypeRankingsFeaturesModel);
      });

      const styleIdsString = getColumnIdsString(columns);

      preloader.resolve(`comparableFeatures["${styleIdsString}"]`, ComparatorModel);
      preloader.resolve(`evEfficiency["${styleIdsString}"]`, ComparatorModel);
      preloader.resolve(`evTaxCredit["${styleIdsString}"]`, ComparatorModel);
      preloader.resolve(`pricing["${styleIdsString}"]`, ComparatorModel);
      preloader.resolve(`editorialReview["${styleIdsString}"]`, ComparatorModel);
      preloader.resolve(`consumerRating["${styleIdsString}"]`, ComparatorModel);
      preloader.resolve(`tco["${getTCOIdsString(vehicles)}"]`, ComparatorModel);
      preloader.resolve(`colors["${styleIdsString}"]`, ComparatorModel);

      store.dispatch(
        setCriticalCss({
          hasVehicle: styleIdsString.split(',').some(styleId => styleId !== NO_STYLE_ID),
        })
      );

      return preloader.load();
    });
  },
};

export default pageDefinition(pageOptions);
